import type { ZudokuConfig } from "zudoku";

const config: ZudokuConfig = {
  aiAssistants: false,
  site: {
    title: "Editions Technology Stack",
    logo: {
      src: {
        light: "/tapir.icon.png",
        dark: "/tapir.icon.png",
      },
    },
    footer: {
      position: "center",
      copyright: `© ${new Date().getFullYear()} Göttingen University. Licensed under CC-BY-NC-4.0`,
    },
  },
  metadata: {
    title: "Editions Technology Stack by SUB Göttingen",
    description:
      "This is the integrated API and technology stack documentation.",
    logo: "/tapir.icon.png",
    favicon: "/tapir.icon.png",
    generator: "Zudoku",
    applicationName: "Editions Technology Stack",
    keywords: ["text", "api", "digital scholarly editions"],
    authors: ["Kristine Schima-Voigt", "Mustafa Dogan"],
    creator: "Göttingen University, State and University Library Göttingen",
    publisher: "Göttingen University, State and University Library Göttingen",
  },
  docs: {
    defaultOptions: {
      copyPage: false, // remove copy button as it features commercial AI only
      fullWidth: true,
      showLastModified: false,
    },
    llms: {
      llmsTxt: true, // Generate llms.txt summary file
      llmsTxtFull: true, // Generate llms-full.txt with complete content
      includeProtected: false,
    },
  },
  catalogs: {
    path: "/catalog",
    label: "API Catalog",
  },
  navigation: [
    {
      type: "category",
      label: "Overview",
      items: [
        {
          type: "category",
          label: "Getting Started",
          icon: "wand-sparkles",
          items: [
            "/introduction",
            "/who-is-it-for",
            "/ecosystem",
            "/showcases",
          ],
        },
        {
          type: "category",
          icon: "text-initial",
          label: "TextAPI",
          items: [
            "/textapi/introduction",
            "/textapi/hierarchy",
            "/textapi/v2-migration-guide",
          ],
        },
        {
          type: "category",
          icon: "message-square-plus",
          label: "AnnotationAPI",
          items: [
            "/annotationapi/introduction",
            "/annotationapi/crossref",
            "/annotationapi/synopsis",
            "/annotationapi/variant",
            "/annotationapi/v2-migration-guide",
          ],
        },
        {
          type: "category",
          icon: "file-search",
          label: "SearchAPI",
          items: [
            "/searchapi/introduction",
          ],
        },
        {
          type: "category",
          icon: "book-marked",
          label: "IndexAPI",
          items: [
            "/indexapi/introduction",
          ],
        },
        {
          type: "category",
          icon: "panels-top-left",
          label: "TIDO",
          items: [
            "/tido/introduction",
            "/tido/getting-started",
            "/tido/configuration",
            "/tido/playground"
          ],
        },
        {
          type: "category",
          label: "References",
          collapsible: false,
          icon: "link",
          items: [
            {
              type: "link",
              icon: "book",
              label: "GitLab Group",
              to: "https://gitlab.gwdg.de/subugoe/textapi",
            },
          ],
        },
      ],
    },
    { type: "separator" },
    { type: "link", label: "Specifications: ", to: "/catalog" },
    {
      type: "link",
      to: "/textapi-specs",
      label: "TextAPI",
    },
    {
      type: "link",
      to: "/annotationapi-specs",
      label: "AnnotationAPI",
    },
    {
      type: "link",
      to: "/searchapi-specs",
      label: "SearchAPI",
    },
  ],
  redirects: [
    { from: "/", to: "/introduction" },
    { from: "/textapi", to: "/textapi/introduction" },
    { from: "/annotationapi", to: "/annotationapi/introduction" },
    { from: "/searchapi", to: "/searchapi/introduction" },
    { from: "/indexapi", to: "/indexapi/introduction" },

  ],
  apis: [
    {
      type: "file",
      input: "./apis/textapi.json",
      path: "/textapi-specs",
      categories: [{ label: "TextAPI", tags: [] }],
      options: {
        schemaDownload: {
          enabled: true,
        },
      },
    },
    {
      type: "file",
      input: "./apis/searchapi.json",
      path: "/searchapi-specs",
      categories: [{ label: "SearchAPI", tags: [] }],
      options: {
        schemaDownload: {
          enabled: true,
        },
      },
    },
    {
      type: "file",
      input: "./apis/annotationapi.json",
      path: "/annotationapi-specs",
      categories: [{ label: "AnnotationAPI", tags: [] }],
      options: {
        schemaDownload: {
          enabled: true,
        },
      },
    },
  ],
  theme: {
    light: {
      background: "oklch(0.992 0.004 286.327)",
      foreground: "oklch(22.31% 0.018 352.74)",
      card: "oklch(0.992 0.004 286.327)",
      cardForeground: "oklch(12.9% 0.027 262)",
      popover: "oklch(0.992 0.004 286.327)",
      popoverForeground: "oklch(22.31% 0.018 352.74)",
      primary: "oklch(53.80% 0.187 312.37)",
      primaryForeground: "oklch(98.4% 0.002 248)",
      secondary: "oklch(96.7% 0.003 265)",
      secondaryForeground: "oklch(21% 0.032 265)",
      muted: "oklch(91.50% 0.022 265.76)",
      mutedForeground: "oklch(55.1% 0.023 264)",
      accent: "oklch(94.01% 0.017 282.54)",
      accentForeground: "oklch(22.31% 0.018 352.74)",
      destructive: "oklch(63.7% 0.208 25.3)",
      destructiveForeground: "oklch(98.4% 0.002 248)",
      border: "oklch(91.50% 0.022 265.76)",
      input: "oklch(92.8% 0.006 265)",
      ring: "oklch(53.80% 0.187 312.37)",
    },
    dark: {
      background: "oklch(17.74% 0.025 276.28)",
      foreground: "oklch(0.992 0.004 286.327)",
      card: "oklch(17.04% 0.031 276.76)",
      cardForeground: "oklch(98.4% 0.002 248)",
      popover: "oklch(12.9% 0.027 262)",
      popoverForeground: "oklch(98.4% 0.002 248)",
      primary: "oklch(53.80% 0.187 312.37)",
      primaryForeground: "oklch(98.4% 0.002 248)",
      secondary: "oklch(27.8% 0.03 257)",
      secondaryForeground: "oklch(98.4% 0.002 248)",
      muted: "oklch(25.61% 0.056 281.73)",
      mutedForeground: "oklch(0.719 0.018 256.305)",
      accent: "oklch(21.14% 0.047 276.19)",
      accentForeground: "oklch(0.992 0.004 286.327)",
      destructive: "oklch(39.6% 0.133 25.7)",
      destructiveForeground: "oklch(98.4% 0.002 248)",
      border: "oklch(24.01% 0.057 275.50)",
      input: "oklch(27.8% 0.03 257)",
      ring: "oklch(53.80% 0.187 312.37)",
    },
  },
  slots: {
    "header-extra": () => <div>Test</div>,
  },
};

export default config;
